import type { AxiosError } from 'axios';
import { ApiError } from './apiError';

/**
 * Retry for brief outages, on requests that are safe to repeat. Only use it for
 * idempotent calls (the server dedupes them, or repeating them changes nothing).
 */

const TRANSIENT_STATUSES: ReadonlySet<number> = new Set([429, 502, 503, 504]);
const MAX_RETRY_AFTER_MS = 10_000;

/**
 * True when the request never got an HTTP answer (network error, timeout) or the
 * server said "try again" (429, 502, 503, 504). Never true for other 4xx or 500.
 * Reads both the raw AxiosError and the ApiError that `withApiError` rethrows.
 */
export function isTransientError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === undefined || TRANSIENT_STATUSES.has(error.status);
  }
  if (typeof error !== 'object' || error === null) return false;
  const axiosError = error as AxiosError;
  if (!axiosError.isAxiosError || axiosError.code === 'ERR_CANCELED') return false;
  const status = axiosError.response?.status;
  return status === undefined || TRANSIENT_STATUSES.has(status);
}

/**
 * Milliseconds to wait before retry number `attempt` (0 for the first retry).
 * Honours a `Retry-After` header in seconds, capped at 10s; otherwise 1s, 2s, 4s.
 */
export function transientRetryDelay(attempt: number, error?: unknown): number {
  const headers = typeof error === 'object' && error !== null ? (error as AxiosError).response?.headers : undefined;
  const seconds = Number.parseFloat(String(headers?.['retry-after']));
  if (Number.isFinite(seconds) && seconds >= 0) return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
  return 1000 * 2 ** attempt;
}

/** Spread into `useMutation({ ..., ...transientRetryOptions() })`. Lower the cap with `maxRetries`. */
export function transientRetryOptions(maxRetries = 3) {
  return {
    retry: (failureCount: number, error: unknown) => failureCount < maxRetries && isTransientError(error),
    retryDelay: transientRetryDelay,
  };
}

/** The same policy for plain async callers that are not TanStack mutations. */
export async function withTransientRetry<T>(fn: () => Promise<T>, maxRetries = 3): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt >= maxRetries || !isTransientError(error)) throw error;
      await new Promise((resolve) => setTimeout(resolve, transientRetryDelay(attempt, error)));
    }
  }
}
